import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router';
import { WorkFocusGate } from '../features/work-focus/components/WorkFocusGate';
import { MonitoringIndicator } from '../features/work-focus/components/MonitoringIndicator';
import { isWorkFocusDisabledInDev } from '../features/work-focus/lib/devBypass';
import { LoanReviewShiftToggle } from './loanReview/LoanReviewShiftToggle';
import { AdminNotificationBell } from './loanReview/AdminNotificationBell';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Stack,
  Snackbar,
  Alert,
  Divider,
  useTheme,
  alpha,
  Chip,
  Button,
} from '@mui/material';
import LibraryBooksRoundedIcon from '@mui/icons-material/LibraryBooksRounded';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
import IntegrationInstructionsRoundedIcon from '@mui/icons-material/IntegrationInstructionsRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SwitchAccountRoundedIcon from '@mui/icons-material/SwitchAccountRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cacheApi } from '../api/admin';
import { adminConsoleAuthApi } from '../api/adminConsole';
import { orchestratorAuthApi, ORCHESTRATOR_AUTH_ME_QUERY_KEY } from '../api/orchestratorAuth';
import {
  ADMIN_CONSOLE_ME_QUERY_KEY,
  isOrchestratorDisabled,
  signOutOperator,
  terminateOperatorSession,
} from '../lib/adminSession';
import { isOidcAdminAuth } from '../lib/oidc/oidcConfig';
import { reauthorizeForMfa, startOidcAuthorize } from '../lib/oidc/oidcSession';
import { decideOrchestratorChain } from '../lib/oidc/orchestratorChainCore';
import {
  clearOrchestratorChainGuard,
  lastOrchestratorChainAt,
  orchestratorChainAttemptedThisPageLoad,
  startOrchestratorOperatorChain,
} from '../lib/oidc/orchestratorChain';
import { buildLoginReturnQuery } from '../config/paths';
import { AppErrorBoundary } from './AppErrorBoundary';
import {
  ADMIN_ORCHESTRATOR_API_ERROR,
  type AdminApiErrorDetail,
} from '../lib/adminApiEvents';
import { resolveAdminGlobalSnackbarMessage } from '../lib/adminApiErrorPolicy';
import {
  ADMIN_MFA_STATE_EVENT,
  ADMIN_MFA_STEPPED_UP_EVENT,
  MFA_BANNER_TEXT,
  MFA_LOGIN_PARAM,
  OIDC_MFA_BANNER_TEXT,
  mfaStepUpFailedFor,
  shouldShowMfaBanner,
} from '../lib/adminMfaStepUp';
import { ADMIN_CONSOLE_TOKEN_EVENT, getAdminConsoleToken } from '../lib/adminConsoleToken';
import { PermissionKey } from '@c4ytechnology/authz';
import { firstAllowedPath, hasAdminPermission, navVisible, pathAllowed } from '../lib/adminPermissions';
import { rememberedApplicationsListPath } from '../lib/applicationsListFilters';
import ThemeShifter from './ThemeShifter';
import { LoanOpsChatPanel } from './loanOps/LoanOpsChatPanel';
import { AdminPageChromeProvider, useAdminPageChromeContext } from '../context/AdminPageChromeContext';
import { AdminTopBarChrome } from './admin/AdminTopBarChrome';
import { ScrollToTopButton } from './ScrollToTopButton';
import { JitActivationBanner } from './admin/iam/JitActivationBanner';

const SIDEBAR_WIDTH = 260;

const NAV: Array<{ to: string; label: string; icon: ReactNode; end?: boolean }> = [
  { to: '/applications',   label: 'Applications',    icon: <AppsRoundedIcon fontSize="small" /> },
  { to: '/doctrine-config', label: 'Doctrine configuration', icon: <LibraryBooksRoundedIcon fontSize="small" /> },
  { to: '/providers',      label: 'Providers',       icon: <HubRoundedIcon fontSize="small" /> },
  { to: '/routing',        label: 'Routing',         icon: <AltRouteRoundedIcon fontSize="small" /> },
  { to: '/service-onboarding', label: 'Service Onboarding', icon: <IntegrationInstructionsRoundedIcon fontSize="small" /> },
  { to: '/vendors',        label: 'Provider Onboarding', icon: <ExtensionRoundedIcon fontSize="small" /> },
  // RBAC Phase 4: every signed-in staff member (activate eligible roles, approve requests).
  { to: '/my-access',     label: 'My access',       icon: <KeyRoundedIcon fontSize="small" /> },
  { to: '/admin/users',   label: 'Users',           icon: <PeopleRoundedIcon fontSize="small" /> },
  { to: '/admin/roles',   label: 'Roles',           icon: <AdminPanelSettingsRoundedIcon fontSize="small" /> },
  { to: '/admin/access/expiring', label: 'Expiring access', icon: <EventBusyRoundedIcon fontSize="small" /> },
  { to: '/admin/access/reviews',  label: 'Access reviews',  icon: <FactCheckRoundedIcon fontSize="small" /> },
  { to: '/admin/access/eligibilities', label: 'Eligibilities', icon: <HowToRegRoundedIcon fontSize="small" /> },
  { to: '/admin/audit-log', label: 'Admin audit',   icon: <ReceiptLongRoundedIcon fontSize="small" /> },
  { to: '/admin/groups',  label: 'Groups',          icon: <GroupsRoundedIcon fontSize="small" /> },
];

export function Layout(): ReactNode {
  return (
    <AdminPageChromeProvider>
      <LayoutShell />
    </AdminPageChromeProvider>
  );
}

function LayoutShell(): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const { chrome } = useAdminPageChromeContext();
  const topBarRef = useRef<HTMLDivElement | null>(null);

  const orchestratorDisabled = isOrchestratorDisabled();
  // RBAC Phase 3: in OIDC mode admin-api pages need only the auth-server token (OidcSessionGate);
  // the orchestrator cookie session no longer gates the console.
  const oidcAuth = isOidcAdminAuth();
  const workFocusDisabled = isWorkFocusDisabledInDev();
  const [apiSnackbar, setApiSnackbar] = useState<AdminApiErrorDetail | null>(null);
  const adminConsoleRetryRef = useRef(false);

  const onOrchestratorApiError = useCallback((e: Event) => {
    if (!(e instanceof CustomEvent)) return;
    const d = e.detail as AdminApiErrorDetail | undefined;
    if (d && typeof d.message === 'string' && typeof d.status === 'number') {
      setApiSnackbar({
        ...d,
        message: resolveAdminGlobalSnackbarMessage(d.message),
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener(ADMIN_ORCHESTRATOR_API_ERROR, onOrchestratorApiError as EventListener);
    return () => window.removeEventListener(ADMIN_ORCHESTRATOR_API_ERROR, onOrchestratorApiError as EventListener);
  }, [onOrchestratorApiError]);

  // RBAC Phase 2: a failed silent MFA step-up (per token) shows the MFA banner; a successful one
  // re-issues the token, so `/auth/me` is refetched for the new `mfa` state.
  const [mfaStepUpFailed, setMfaStepUpFailed] = useState(() => mfaStepUpFailedFor(getAdminConsoleToken()));
  useEffect(() => {
    const sync = () => setMfaStepUpFailed(mfaStepUpFailedFor(getAdminConsoleToken()));
    const steppedUp = () => {
      sync();
      void qc.invalidateQueries({ queryKey: ADMIN_CONSOLE_ME_QUERY_KEY });
    };
    window.addEventListener(ADMIN_MFA_STATE_EVENT, sync);
    window.addEventListener(ADMIN_CONSOLE_TOKEN_EVENT, sync);
    window.addEventListener(ADMIN_MFA_STEPPED_UP_EVENT, steppedUp);
    return () => {
      window.removeEventListener(ADMIN_MFA_STATE_EVENT, sync);
      window.removeEventListener(ADMIN_CONSOLE_TOKEN_EVENT, sync);
      window.removeEventListener(ADMIN_MFA_STEPPED_UP_EVENT, steppedUp);
    };
  }, [qc]);

  useLayoutEffect(() => {
    const el = topBarRef.current;
    if (!el) return;
    const sync = () => {
      document.documentElement.style.setProperty('--admin-topbar-height', `${el.offsetHeight}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--admin-topbar-height');
    };
  }, [chrome]);

  const orchestratorMe = useQuery({
    queryKey: ORCHESTRATOR_AUTH_ME_QUERY_KEY,
    queryFn: () => orchestratorAuthApi.me(),
    enabled: typeof window !== 'undefined' && !orchestratorDisabled,
    retry: false,
    refetchOnWindowFocus: true,
  });

  const adminConsoleMe = useQuery({
    queryKey: ADMIN_CONSOLE_ME_QUERY_KEY,
    queryFn: () => adminConsoleAuthApi.me(),
    enabled:
      typeof window !== 'undefined' &&
      (oidcAuth || orchestratorDisabled || orchestratorMe.data?.authenticated === true),
    retry: false,
    refetchOnWindowFocus: true,
  });

  // RBAC Phase 3 (OIDC mode): chain a silent orchestrator operator sign-in for the
  // orchestrator-backed pages; once per page load / 2 min, then a non-blocking notice.
  const orchestratorChain = decideOrchestratorChain({
    oidcMode: oidcAuth,
    orchestratorDisabled,
    session: orchestratorMe.isSuccess && !orchestratorMe.isFetching ? orchestratorMe.data : undefined,
    adminSessionReady: !!adminConsoleMe.data,
    attemptedThisPageLoad: orchestratorChainAttemptedThisPageLoad(),
    lastAttemptAtMs: lastOrchestratorChainAt(),
    nowMs: Date.now(),
  });
  useEffect(() => {
    if (orchestratorChain === 'start') startOrchestratorOperatorChain();
  }, [orchestratorChain]);
  const [orchestratorNoticeDismissed, setOrchestratorNoticeDismissed] = useState(false);

  const signOut = useMutation({
    mutationFn: () => signOutOperator(qc),
    onSuccess: () => {
      // OIDC mode is already navigating to auth-server end_session.
      if (!oidcAuth) void navigate('/login', { replace: true });
    },
  });

  useEffect(() => {
    if (location.pathname === '/login' || location.pathname.startsWith('/login/')) return;
    // MFA routes are siblings of Layout; still guard if routing changes.
    if (location.pathname.startsWith('/mfa')) return;

    const ret = encodeURIComponent(buildLoginReturnQuery(location.pathname, location.search));
    const goSignIn = () => {
      void navigate(`/login?return=${ret}`, { replace: true });
    };

    if (!orchestratorDisabled && !oidcAuth) {
      if (orchestratorMe.isPending || orchestratorMe.isFetching) return;
      if (orchestratorMe.data?.mfaChallengePending) return;
      if (orchestratorMe.data?.authenticated === false) {
        // Cookie already missing or expired — do not POST /auth/logout (that
        // polls /auth/me and storms 401s with empty cookies).
        goSignIn();
        return;
      }
    }

    const adminAuthRequired =
      oidcAuth || orchestratorDisabled || orchestratorMe.data?.authenticated === true;
    if (!adminAuthRequired) return;

    if (adminConsoleMe.isPending || adminConsoleMe.isFetching) return;
    if (adminConsoleMe.isError || !adminConsoleMe.data) {
      if (!adminConsoleRetryRef.current) {
        adminConsoleRetryRef.current = true;
        void qc.invalidateQueries({ queryKey: ADMIN_CONSOLE_ME_QUERY_KEY });
        return;
      }
      void terminateOperatorSession(qc).finally(goSignIn);
    } else {
      adminConsoleRetryRef.current = false;
      const me = adminConsoleMe.data;
      if (
        me &&
        location.pathname !== '/no-access' &&
        !pathAllowed(location.pathname, me)
      ) {
        void navigate(firstAllowedPath(me), { replace: true });
      }
    }
  }, [
    oidcAuth,
    orchestratorDisabled,
    orchestratorMe.isPending,
    orchestratorMe.isFetching,
    orchestratorMe.data?.authenticated,
    orchestratorMe.data?.mfaChallengePending,
    adminConsoleMe.isPending,
    adminConsoleMe.isFetching,
    adminConsoleMe.isError,
    adminConsoleMe.data,
    location.pathname,
    location.search,
    navigate,
    qc,
  ]);

  const status = useQuery({
    queryKey: ['cacheStatus'],
    queryFn: () => cacheApi.status(),
    refetchInterval: 30_000,
    // The orchestrator gates cache/status on doctrine.read; without it the chip would only 403.
    enabled:
      !orchestratorDisabled &&
      // OIDC mode: only when an orchestrator cookie session happens to exist (no 401 storm).
      (!oidcAuth || orchestratorMe.data?.authenticated === true) &&
      !!adminConsoleMe.data &&
      hasAdminPermission(adminConsoleMe.data, PermissionKey.DOCTRINE_READ),
  });

  const sessionActive =
    (oidcAuth || orchestratorDisabled || orchestratorMe.data?.authenticated === true) &&
    !!adminConsoleMe.data;
  const sessionEmail = oidcAuth
    ? adminConsoleMe.data?.email
    : orchestratorMe.data?.email ?? orchestratorMe.data?.userId;

  // Solid deep blue-purple sidebar (no gradient) — matches filter panel brand
  const NAV_PURPLE_BLUE = '#312E81'; // keep in sync with ADMIN_NAV_SURFACE in adminListPageUi
  const navBg = NAV_PURPLE_BLUE;
  const navTextMuted = 'rgba(255,255,255,0.78)';
  const navDivider = 'rgba(255,255,255,0.22)';
  const navHover = 'rgba(255,255,255,0.14)';
  const navActive = 'rgba(255,255,255,0.22)';

  const goLogin = () => {
    if (oidcAuth) {
      // Switch account = sign out at auth-server (end_session) → /login → sign in again.
      if (sessionActive) signOut.mutate();
      else void startOidcAuthorize({ kind: 'interactive' });
      return;
    }
    const ret = encodeURIComponent(buildLoginReturnQuery(location.pathname, location.search));
    void navigate(`/login?return=${ret}`);
  };

  const showMfaBanner =
    sessionActive && shouldShowMfaBanner({ me: adminConsoleMe.data, stepUpFailed: mfaStepUpFailed });

  /**
   * The password sign-in flow (orchestrator password → authenticator challenge), then back here.
   * `mfa=1` makes /login steer away from Google/BizAuth: an SSO session never gets an MFA
   * attestation, so it would land straight back on this banner.
   */
  const signInAgainWithMfa = () => {
    if (oidcAuth) {
      // max_age=0&prompt=login&acr_values=mfa, back to this route.
      reauthorizeForMfa();
      return;
    }
    const ret = encodeURIComponent(buildLoginReturnQuery(location.pathname, location.search));
    void terminateOperatorSession(qc).finally(() => {
      void navigate(`/login?return=${ret}&${MFA_LOGIN_PARAM}=1`, { replace: true });
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      {/* ── Sticky Sidebar ────────────────────────────────────────────── */}
      <Box
        component="nav"
        sx={{
          width: { xs: 1, md: SIDEBAR_WIDTH },
          flexShrink: 0,
          position: { xs: 'static', md: 'sticky' },
          top: 0,
          height: { xs: 'auto', md: '100vh' },
          display: 'flex',
          flexDirection: 'column',
          bgcolor: navBg,
          borderRight: { xs: 0, md: `1px solid ${alpha('#000', 0.18)}` },
          borderBottom: { xs: `1px solid ${navDivider}`, md: 0 },
          boxShadow: {
            xs: 'none',
            md: `6px 0 28px ${alpha(NAV_PURPLE_BLUE, 0.45)}`,
          },
          overflow: { xs: 'visible', md: 'hidden' },
        }}
      >
        {/* Logo block */}
        <Box sx={{ p: { xs: 1.25, md: 2.5 }, pb: { xs: 1, md: 2 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5} justifyContent={{ xs: 'space-between', md: 'flex-start' }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                bgcolor: 'rgba(255,255,255,0.16)',
                border: '1.5px solid rgba(255,255,255,0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.9rem', lineHeight: 1 }}>
                C4U
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: '1.1rem',
                color: '#fff',
                lineHeight: 1.2,
              }}
            >
              Admin
            </Typography>
            <Box sx={{ display: { xs: 'block', md: 'none' }, ml: 'auto' }}>
              <ThemeShifter size="small" color="#fff" />
            </Box>
          </Stack>

          {/* Config version chip */}
          {status.data?.configVersion && (
            <Chip
              size="small"
              label={`config v${status.data.configVersion.slice(0, 12)}…`}
              variant="outlined"
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                mt: 1.5,
                fontSize: '0.65rem',
                height: 20,
                color: navTextMuted,
                borderColor: navDivider,
              }}
            />
          )}
        </Box>

        <Divider sx={{ borderColor: navDivider }} />

        {/* Nav items */}
        <List
          dense
          sx={{
            flex: { xs: '0 0 auto', md: 1 },
            overflow: 'auto',
            display: { xs: 'flex', md: 'block' },
            gap: { xs: 0.5, md: 0 },
            px: { xs: 1, md: 1.5 },
            py: { xs: 0.75, md: 1 },
          }}
        >
          {NAV.filter((item) => navVisible(item.to, adminConsoleMe.data)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to === '/applications' ? rememberedApplicationsListPath() : item.to}
              end={item.end}
              style={{ textDecoration: 'none', color: 'inherit', flexShrink: 0 }}
            >
              {({ isActive }) => (
                <ListItemButton
                  sx={{
                    borderRadius: '10px',
                    mb: { xs: 0, md: 0.5 },
                    minWidth: { xs: 132, md: 'auto' },
                    px: 1.5,
                    py: 0.95,
                    bgcolor: isActive ? navActive : 'transparent',
                    border: `2px solid ${isActive ? 'rgba(255,255,255,0.55)' : 'transparent'}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: isActive ? navActive : navHover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      color: '#fff',
                      '& svg': { fontSize: '1.15rem' },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isActive ? 800 : 700,
                      letterSpacing: '0.01em',
                      color: '#fff',
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              )}
            </NavLink>
          ))}
        </List>

        <Divider sx={{ borderColor: navDivider }} />

        {/* Single session control (orchestrator cookie + best-effort admin-api JWT from same login) */}
        <Box sx={{ px: { xs: 1, md: 1.5 }, pt: { xs: 0.75, md: 1.25 }, pb: { xs: 1, md: 1.25 } }}>
          <Typography
            variant="caption"
            sx={{
              display: { xs: 'none', md: 'block' },
              px: 0.5,
              fontWeight: 600,
              color: navTextMuted,
            }}
          >
            Session
          </Typography>
          {orchestratorDisabled && (
            <Typography variant="caption" sx={{ display: 'block', px: 0.5, mt: 0.5, color: '#FDE68A' }}>
              Orchestrator stubs — no live session
            </Typography>
          )}
          <Stack direction={{ xs: 'row', md: 'column' }} spacing={{ xs: 0.75, md: 0 }}>
            <ListItemButton
              onClick={goLogin}
              sx={{
                borderRadius: '10px',
                px: 1.25,
                py: { xs: 0.75, md: 1 },
                mt: { xs: 0, md: 0.75 },
                minHeight: { xs: 44, md: 58 },
                flex: { xs: 1, md: '0 0 auto' },
                minWidth: 0,
                color: sessionActive ? '#A7F3D0' : '#fff',
                border: `1px solid ${
                  sessionActive ? alpha('#34D399', 0.55) : navDivider
                }`,
                bgcolor: sessionActive ? alpha('#10B981', 0.22) : 'rgba(255,255,255,0.08)',
                '&:hover': {
                  bgcolor: sessionActive ? alpha('#10B981', 0.3) : navHover,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>
                {sessionActive ? (
                  <SwitchAccountRoundedIcon sx={{ fontSize: '1.2rem' }} />
                ) : (
                  <LoginRoundedIcon sx={{ fontSize: '1.2rem' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={sessionActive ? 'Switch account' : 'Sign in'}
                secondary={
                  sessionActive
                    ? sessionEmail
                    : undefined
                }
                primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 800, noWrap: true, color: 'inherit' }}
                secondaryTypographyProps={{
                  fontSize: '0.68rem',
                  color: sessionActive ? '#6EE7B7' : navTextMuted,
                  noWrap: true,
                }}
              />
            </ListItemButton>
            {sessionActive && (
              <ListItemButton
                onClick={() => signOut.mutate()}
                disabled={signOut.isPending}
                sx={{
                  borderRadius: '10px',
                  px: 1.5,
                  py: 0.65,
                  mt: { xs: 0, md: 0.5 },
                  flex: { xs: '0 0 auto', md: '0 0 auto' },
                  color: '#FCA5A5',
                  '&:hover': { bgcolor: alpha('#EF4444', 0.18) },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>
                  <LogoutRoundedIcon sx={{ fontSize: '0.95rem' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Sign out"
                  primaryTypographyProps={{ fontSize: '0.76rem', fontWeight: 600, noWrap: true, color: 'inherit' }}
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                />
              </ListItemButton>
            )}
          </Stack>
        </Box>

      </Box>

      {/* ── Main area ─────────────────────────────────────────────────── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          bgcolor: 'background.default',
        }}
      >
        {/* Slim top bar — page chrome (title/breadcrumbs/actions) + theme */}
        <Box
          ref={topBarRef}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            px: { xs: 1.5, md: 3 },
            py: chrome ? 0.85 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minHeight: chrome ? 52 : 47,
            // Paper surface — was same as main canvas (#EDEFF3) and disappeared into content
            bgcolor: isLight
              ? alpha('#FFFFFF', 0.92)
              : alpha(theme.palette.background.paper, 0.94),
            backdropFilter: 'blur(14px)',
            borderBottom: `1.5px solid ${alpha(theme.palette.primary.main, isLight ? 0.18 : 0.32)}`,
            boxShadow: isLight
              ? `0 4px 18px ${alpha(theme.palette.primary.main, 0.07)}`
              : `0 6px 22px ${alpha('#000', 0.35)}`,
          }}
        >
          <AdminTopBarChrome chrome={chrome} />
          {!chrome ? <Box sx={{ flex: 1 }} /> : null}
          <LoanReviewShiftToggle />
          <AdminNotificationBell enabled={sessionActive} />
          {workFocusDisabled || adminConsoleMe.data?.isMonitored === false ? null : (
            <MonitoringIndicator />
          )}
          <Box sx={{ display: { xs: 'none', md: 'block' }, flexShrink: 0 }}>
            <ThemeShifter size="small" />
          </Box>
        </Box>

        {showMfaBanner && (
          <Alert
            severity="warning"
            role="status"
            sx={{ mx: { xs: 1.25, sm: 2, md: 3 }, mt: { xs: 1.25, md: 2 }, mb: 0 }}
            action={
              <Button color="inherit" size="small" onClick={signInAgainWithMfa} sx={{ fontWeight: 700 }}>
                Sign in again
              </Button>
            }
          >
            {oidcAuth ? OIDC_MFA_BANNER_TEXT : MFA_BANNER_TEXT}
          </Alert>
        )}

        {orchestratorChain === 'unavailable' && !orchestratorNoticeDismissed && (
          <Alert
            severity="info"
            role="status"
            onClose={() => setOrchestratorNoticeDismissed(true)}
            sx={{ mx: { xs: 1.25, sm: 2, md: 3 }, mt: { xs: 1.25, md: 2 }, mb: 0 }}
            action={
              <Button
                color="inherit"
                size="small"
                sx={{ fontWeight: 700 }}
                onClick={() => {
                  clearOrchestratorChainGuard();
                  startOrchestratorOperatorChain();
                }}
              >
                Retry
              </Button>
            }
          >
            Some features (doctrines, providers, routing, the assistant) use the orchestrator, which could not
            start a session for you. Everything else keeps working.
          </Alert>
        )}

        {/* Page content */}
        {/* The assistant launcher and the back-to-top button are fixed to the bottom-right,
            so the last rows of a scrolled page sat underneath them. Reserve their height
            (56px button + 24px inset) plus a little breathing room. */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            p: { xs: 1.25, sm: 2, md: 3 },
            pt: { xs: 1.25, md: chrome ? 2 : 3 },
            pb: { xs: 12, md: 13 },
          }}
        >
          <AppErrorBoundary>
            <JitActivationBanner enabled={adminConsoleMe.isSuccess} />
            {/* Enrolment gate + headless monitoring for every authenticated page. */}
            {workFocusDisabled ? (
              <Outlet />
            ) : (
              <WorkFocusGate
                subjectId={sessionEmail ?? null}
                displayName={(oidcAuth ? adminConsoleMe.data?.email : orchestratorMe.data?.email) ?? undefined}
                monitoringEnabled={
                  adminConsoleMe.isSuccess ? (adminConsoleMe.data?.isMonitored ?? true) : false
                }
                // The way out of a lock that face verification cannot clear — a broken camera
                // being the obvious one. Reuses the same sign-out the top bar uses.
                onSignOut={() => signOut.mutate()}
              >
                <Outlet />
              </WorkFocusGate>
            )}
          </AppErrorBoundary>
        </Box>
      </Box>

      {/* ── Global API error snackbar ──────────────────────────────────── */}
      <Snackbar
        open={!!apiSnackbar}
        autoHideDuration={10_000}
        onClose={() => setApiSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {apiSnackbar ? (
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setApiSnackbar(null)}
            sx={{ width: '100%', maxWidth: 520 }}
          >
            {apiSnackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      <ScrollToTopButton />
      <LoanOpsChatPanel />
    </Box>
  );
}
